import React, { useEffect, useState } from 'react';
import MainLayout from '../../layout/MainLayout';
import { api } from '../../api/axios';
import { Smartphone, Save, AlertTriangle } from 'lucide-react';

// Maps the nested GET /app-config response into the flat shape PATCH expects.
const toForm = (d = {}) => ({
    forceUpdateEnabled: d.forceUpdateEnabled ?? true,
    androidMinVersionCode: d.android?.minVersionCode ?? 1,
    androidLatestVersionCode: d.android?.latestVersionCode ?? 1,
    androidLatestVersionName: d.android?.latestVersionName ?? '',
    androidStoreUrl: d.android?.storeUrl ?? '',
    iosMinBuild: d.ios?.minBuild ?? 1,
    iosLatestBuild: d.ios?.latestBuild ?? 1,
    iosStoreUrl: d.ios?.storeUrl ?? '',
    forceUpdateTitle: d.forceUpdateTitle ?? '',
    forceUpdateMessage: d.forceUpdateMessage ?? '',
});

export default function AppConfig() {
    const [form, setForm] = useState(toForm());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [error, setError] = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.get('/app-config');
            setForm(toForm(res.data));
        } catch (err) {
            console.error('app-config fetch error:', err);
            setError('Could not load app config.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const set = (key) => (e) => {
        const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [key]: v }));
    };

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            const payload = {
                ...form,
                androidMinVersionCode: Number(form.androidMinVersionCode),
                androidLatestVersionCode: Number(form.androidLatestVersionCode),
                iosMinBuild: Number(form.iosMinBuild),
                iosLatestBuild: Number(form.iosLatestBuild),
            };
            await api.patch('/app-config', payload);
            setSavedAt(new Date());
        } catch (err) {
            console.error('app-config save error:', err);
            setError(err?.response?.data?.message || 'Could not save app config.');
        } finally {
            setSaving(false);
        }
    };

    const numField = (label, key, hint) => (
        <div>
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <input
                type="number"
                value={form[key]}
                onChange={set(key)}
                className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
            {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
        </div>
    );

    const textField = (label, key, hint, textarea) => (
        <div>
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            {textarea ? (
                <textarea
                    rows={3}
                    value={form[key]}
                    onChange={set(key)}
                    className="mt-1 w-full border rounded px-3 py-2 text-sm"
                />
            ) : (
                <input
                    type="text"
                    value={form[key]}
                    onChange={set(key)}
                    className="mt-1 w-full border rounded px-3 py-2 text-sm"
                />
            )}
            {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
        </div>
    );

    return (
        <MainLayout title="App Update" subtitle="Force-update gate for the mobile app">
            {loading ? (
                <div className="text-sm text-gray-500">Loading…</div>
            ) : (
                <div className="max-w-2xl space-y-6">
                    {error && (
                        <div className="flex items-center gap-2 rounded bg-red-50 text-red-700 text-sm px-3 py-2">
                            <AlertTriangle size={16} /> {error}
                        </div>
                    )}

                    <div className="rounded-lg border bg-amber-50 p-3 text-xs text-amber-800">
                        Users whose installed Android <b>versionCode</b> is below the minimum below will be
                        blocked with an update screen until they update. Set the minimum to the new build's
                        versionCode after that build is live on the Play Store (e.g. set to <b>44</b> once the
                        PayU build ships) to force everyone off the old version.
                    </div>

                    <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                        <input
                            type="checkbox"
                            checked={form.forceUpdateEnabled}
                            onChange={set('forceUpdateEnabled')}
                        />
                        Force-update gate enabled (master switch)
                    </label>

                    <section className="space-y-4 rounded-lg border p-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                            <Smartphone size={16} /> Android
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {numField('Minimum versionCode (force below this)', 'androidMinVersionCode',
                                'Installed build < this ⇒ forced update.')}
                            {numField('Latest versionCode', 'androidLatestVersionCode',
                                'Used for an optional "update available" prompt.')}
                            {textField('Latest versionName', 'androidLatestVersionName', 'e.g. 3.4.0')}
                            {textField('Play Store URL', 'androidStoreUrl')}
                        </div>
                    </section>

                    <section className="space-y-4 rounded-lg border p-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                            <Smartphone size={16} /> iOS (not yet shipped)
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {numField('Minimum build', 'iosMinBuild')}
                            {numField('Latest build', 'iosLatestBuild')}
                            {textField('App Store URL', 'iosStoreUrl')}
                        </div>
                    </section>

                    <section className="space-y-4 rounded-lg border p-4">
                        <div className="text-sm font-semibold text-gray-700">Update screen message</div>
                        {textField('Title', 'forceUpdateTitle')}
                        {textField('Message', 'forceUpdateMessage', null, true)}
                    </section>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={save}
                            disabled={saving}
                            className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                        >
                            <Save size={16} /> {saving ? 'Saving…' : 'Save'}
                        </button>
                        {savedAt && (
                            <span className="text-xs text-green-600">
                                Saved at {savedAt.toLocaleTimeString()}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </MainLayout>
    );
}
